
import { Observable, throwError as observableThrowError } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { LocalStorageService } from 'angular-2-local-storage';
import { LocationService } from './location.service';
import { LiveApiError, LiveStreamDetails, LiveRestream } from 'app/interfaces/live';
import { catchError, map } from 'rxjs/operators';
import { OverlayService, DialogDefinition } from './overlay.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class LiveApiService {

	liveApiLocation: string;

	constructor(
		private localStorageService: LocalStorageService,
		private http: HttpClient,
		private locationService: LocationService,
		private overlayService: OverlayService,
		private translate: TranslateService
	) {
		this.liveApiLocation = `${this.locationService.server}/live`;
	}

	getSessionToken(): string {
		const sessToken = this.localStorageService.get('ovp-sessionToken');
		return ((sessToken && sessToken.toString().length > 0) ? sessToken.toString() : 'set');
	}

	determineRequestOptions(extraHeaders?: any) {
		const sessToken = this.getSessionToken();
		let headers = new HttpHeaders();
		headers = headers.append('sesstoken', sessToken);
		let params = new HttpParams();

		if (extraHeaders && Object.keys(extraHeaders).length) {
			Object.keys(extraHeaders).forEach((key) => {
				if (key === 'params') {
					const extraParams = extraHeaders[key];
					if (extraParams && Object.keys(extraParams).length) {
						Object.keys(extraParams).forEach((param) => {
							params = params.set(param, extraParams[param]);
						});
					}
				} else {
					headers = headers.append(key, extraHeaders[key]);
				}
			});
		}
		return { headers, withCredentials: true, params };
	}

	postUrl(url: string, params?: HttpParams | any): Observable<any> {
		const requestOptions = this.determineRequestOptions();

		return this.http.post(this.liveApiLocation + url, params, requestOptions)
			.pipe(
				catchError(
					(err: any) => observableThrowError(err.error as LiveApiError)
				)
			);
	}

	putUrl(url: string, params?: HttpParams | any): Observable<any> {
		const requestOptions = this.determineRequestOptions();

		return this.http.put(this.liveApiLocation + url, params, requestOptions)
			.pipe(
				catchError(
					(err: any) => observableThrowError(err.error as LiveApiError)
				)
			);
	}

	deleteUrl(url: string): Observable<any> {
		const requestOptions = this.determineRequestOptions();

		return this.http.delete(this.liveApiLocation + url, requestOptions)
			.pipe(
				catchError(
					(err: any) => observableThrowError(err.error as LiveApiError)
				)
			);
	}

	getUrl(url: string, params?: HttpParams | any): Observable<any> {
		const requestOptions = this.determineRequestOptions();
		if (params) {
			requestOptions.params = params;
		}

		return this.http.get(this.liveApiLocation + url, requestOptions)
			.pipe(
				catchError(
					(err: any) => observableThrowError(err.error as LiveApiError)
				)
			);
	}

	pauseStream(uuid: string): Observable<any> {
		return this.putUrl(`/stream/${uuid}/pause`);
	}

	resumeStream(uuid: string): Observable<any> {
		return this.putUrl(`/stream/${uuid}/resume`);
	}

	updateRecordingState(uuid: string, desiredStatus: boolean): Observable<any> {
		return this.putUrl(`/stream/${uuid}/recording`, { status: desiredStatus });
	}

	getStream(uuid: string): Observable<any> {
		return this.getUrl(`/stream/${uuid}`);
	}

	saveStream(pristine: LiveStreamDetails, details: LiveStreamDetails): Observable<boolean> {
		return new Observable<boolean>((observer) => {
			if (pristine && pristine.data && details && details.data) {
				const remainingActions: string[] = [];

				const pendingStreamStateChange = details.data.state !== pristine.data.state;
				const pendingRecordingStateChange = details.data.recording !== pristine.data.recording;

				const finishAction = (action: string) => {
					remainingActions.splice(remainingActions.indexOf(action), 1);
					if (remainingActions.length === 0) {
						observer.next(true);
						observer.complete();
					}
				};

				if (pendingStreamStateChange) {
					remainingActions.push('stream-state-change');
				}

				if (pendingRecordingStateChange) {
					remainingActions.push('recording-state-change');
				}

				if (pendingStreamStateChange) {
					const streamStateObservable = (details.data.state === 'available') ? this.resumeStream(details.data.id) : this.pauseStream(details.data.id);

					streamStateObservable.subscribe(
						(res) => {
							pristine.data.state = details.data.state;
						},
						(err) => {
							details.data.state = pristine.data.state;
						},
						() => {
							finishAction('stream-state-change');
						});
				}

				// Recording status has changed
				if (pendingRecordingStateChange) {
					// In case we're also updating the stream state, we wait a bit with this request as it's dependent on stream state
					setTimeout(() => {
						this.updateRecordingState(details.data.id, details.data.recording).subscribe(
							(res) => {
								pristine.data.recording = details.data.recording;
							},
							(err) => {
								details.data.recording = pristine.data.recording;
							},
							() => {
								finishAction('recording-state-change');
							}
						);
					}, (details.data.state !== pristine.data.state) ? 500 : 0);
				}
			} else {
				observer.next(false);
				observer.complete();
			}
		});
	}

	getRestreams(uuid: string): Observable<any> {
		return this.getUrl(`/stream/${uuid}/restreams`);
	}

	saveRestream(uuid: string, restream: LiveRestream): Observable<any> {
		const urlRegex = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/g;

		if (restream['public-url'] && !urlRegex.test(restream['public-url'])) {
			this.overlayService.createToast(
				this.translate.instant('ERROR_UPDATING_ENTITY2',
					{
						entity: this.translate.instant('RESTREAM').toLowerCase(),
						errorMessage: 'The given target URL was not a valid URL.', errorCode: 'invalid-format'
					})
			);
			return;
		}

		if (restream.id === 'new') {
			return this.postUrl(`/stream/${uuid}/restreams/`, restream).pipe(
				map((res: any) => {
					if (res.data.id) {
						restream.id = res.data.id;
					}
					this.overlayService.createToast(
						this.translate.instant('SUCCESS_CREATING_ENTITY2', { entity: this.translate.instant('RESTREAM').toLowerCase() })
					);
				}),
				catchError((error: any) => {
					if (error.error) {
						error = error.error;

						const errorMessage = this.translate.instant('ERROR_UPDATING_ENTITY2', {
							entity: this.translate.instant('RESTREAM').toLowerCase(),
							errorMessage: error.message,
							errorCode: error.code
						});

						this.overlayService.createToast(errorMessage);
					}

					return observableThrowError(error);
				})
			);
		} else {
			return this.putUrl(`/stream/${uuid}/restream/${restream.id}/state`, { state: restream.state }).pipe(
				map((res: any) => {
					this.overlayService.createToast(
						this.translate.instant('SUCCESS_UPDATING_ENTITY2', { entity: this.translate.instant('RESTREAM').toLowerCase(), id: restream.id })
					);
				}),
				catchError((error: any) => {
					if (error.error) {
						error = error.error;

						const errorMessage = this.translate.instant('ERROR_UPDATING_ENTITY2', {
							entity: this.translate.instant('RESTREAM').toLowerCase(),
							errorMessage: error.message,
							errorCode: error.code
						});

						this.overlayService.createToast(errorMessage);
					}
					return observableThrowError(error);
				})
			);
		}
	}

	deleteRestream(uuid: string, restream: LiveRestream): Observable<any> {
		const dialogConfirmed = new Observable((observer) => {
			const dialog: DialogDefinition = {
				icon: 'trash',
				title: this.translate.instant('DELETE_RESTREAM', {
					id: restream.id
				}),
				contents: this.translate.instant(
					'DELETE_ENTITY_CONFIRM', { entity: this.translate.instant('RESTREAM').toLowerCase(), id: restream.title || restream.id }
				),
				onConfirm: () => {
					this.deleteUrl(`/stream/${uuid}/restream/${restream.id}`).subscribe(
						(data: any) => {
							this.overlayService.createToast(this.translate.instant(
								'ENTITY_DELETED', { id: restream.title || restream.id, entity: this.translate.instant('RESTREAM').toLowerCase() })
							);
						},
						(error: any) => {
							this.overlayService.createToast(this.translate.instant('ERROR_DELETING_ENTITY2',
								{ entity: this.translate.instant('RESTREAM').toLowerCase(), errorMessage: error.message, errorCode: error.code }));
						});

					observer.next(true);
				},
				onCancel: () => {
					observer.complete();
				}
			};

			this.overlayService.createDialog(dialog);
		});

		return dialogConfirmed;
	}
}
